<template>
	<div class="subWrap" :class="typeContainer">
		<div class="top-head">
			<van-icon name="arrow-left" size="1.5rem" color="#86909c" @click="goBack" />
			<template v-if="showCategoryTitle">
				<div class="title-row">{{ titles }}</div>
			</template>
			<template v-else>
				<SearchFake
					:firstcategory="firstcategory"
					:secondcategory="secondcategory"
					:thirdcategory="thirdcategory"
					:productnature="productnature"
					:titles="titles"
					:placeholders="placeholders"
					:companyId="companyId"
				/>
			</template>

			<span style="margin-left: 8px">
				<i class="icon-v2_liebiao iconfont" @click="toggleTo('list')" v-if="viewType == 'grid'"></i>
				<i class="icon-v2_jiugongge iconfont" @click="toggleTo('grid')" v-if="viewType == 'list'"></i>
			</span>
		</div>
		<div>
			<SortBar @sort="handleSortClick" :mainTitle="mainTitle" :companyId="queryParams.companyId">
				<template #icon>
					<!-- v-show 保证 SSR/CSR 子节点一致，避免 Hydration children mismatch -->
					<a v-show="ifMan" rel="nofollow" href="javascript:void(0)" @click.prevent="showDrawer()">
						<i class="iconfont icon-gongneng_shaixuan" :class="{ active: activeFilter }"></i>
					</a>
				</template>
			</SortBar>
		</div>
		<PaginationList
			ref="paginationListRef"
			:fetch-data="fetchProductList"
			:initial-list="ssrListRes?.data?.list ?? []"
			:page-size="PAGE_SIZE"
			:margin-bottom="0"
			enableRefresh
			:scroll-container-style="{ height: 'calc(100vh - 7rem)' }"
		>
			<!-- BigImg/SmallImg 的 dataList 来源：fetchProductListApi -> getMobileCategoryFromSearch(reqParams) -->
			<template #list="{ list: productList }">
				<SmallImg showCart v-if="viewType == 'list'" :dataList="productList" :imgWidth="100" :imgHeight="100" fromPage="商品搜索列表" />
				<BigImg showCart v-if="viewType == 'grid'" :dataList="productList" fromPage="商品搜索列表" />
			</template>
		</PaginationList>
		<SearchDrawer v-model="showPopup" v-if="ifMan" :manufacturer="allManufacturerList" @update="updateDataList" @close="showPopup = false" />
		<SubSiteSelect ref="subSiteRef" :companyId="companyId" />
		<SubFoot v-if="footerType == 'child'" />
	</div>
</template>

<script setup>
import { ref, reactive, computed, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { cloneDeep } from 'lodash-es';
import { getGoodsPricePre, getMobileCategoryFromSearch, getMutil } from '@/api/index.js';
import { getDiscountList } from '@/api/goods.js';
import { getUserSite } from '@/api/index.js';
import { getCompanyInfo } from '@/common/common.js';
import { navigateBack } from '@/utils/utils';
import { showToast } from 'vant';
import SearchDrawer from './components/SearchDrawer.vue';
import SortBar from './components/SortBar.vue';
import BigImg from '@/components/BigImg.vue';
import SubSiteSelect from './components/SubSiteSelect.vue';
import SmallImg from '@/components/SmallImg.vue';
import SubFoot from '@/components/SubFoot.vue';
import PaginationList from '@/components/core/PaginationList.vue';
import { getStorageSync, setStorageSync } from '@/utils/storage.js';
import { mergeProductPriceFields } from '@/utils/productPrice';
import { useAuthStore } from '@/stores/auth';
import { useAnalytics } from '@/composables/useAnalytics';

const authStore = useAuthStore();
const { trackEvent } = useAnalytics();

/** 已登录才可调用 getGoodsPricePre（仅客户端有 token，SSR 不请求该接口） */
const canFetchGoodsPricePre = () => {
	if (!import.meta.client) return false;
	return authStore.isLoggedIn || !!getStorageSync('cmdc_access_token_mobile');
};

const props = defineProps({
	/** 子站传入时使用路径中的 companyId，总站不传则从 route.query 取 */
	initialCompanyId: { type: [String, Number], default: '' },
	initialCategoryTitle: { type: String, default: '' },
});

const route = useRoute();
const categoryDetailTitleState = useState(`category-detail-title:${route.fullPath}`, () => '');

const getCategoryKeywordFromResponse = data => {
	const subCategoryName = data?.subCategoryName ?? '';
	return subCategoryName;
};

const getDetailTitleFromResponse = data => {
	const categoryKeyword = getCategoryKeywordFromResponse(data);
	const thirdCategoryTitle = Array.isArray(data?.thirdcategory) ? String(data.thirdcategory[0] ?? '').trim() : '';
	return categoryKeyword || thirdCategoryTitle || '分类详情';
};

const PAGE_SIZE = 10;

const footerType = ref('main');
const viewType = ref('list');
const showPopup = ref(false);
const queryParams = reactive({
	filterConditions: [],
	companyCode: '',
	ranking: '',
	searchName: '',
	pageNum: 1,
	pageSize: 10,
	companyId: '',
	orderBy: '',
	orderByType: '',
	topSortBarParams: {},
});
const pageFrom = ref('');
const titles = ref('');
const companyId = ref('');
const companyCode = ref('');
const placeholders = ref('');
const isLogin = ref(null);
const productnature = ref(null);
const firstcategory = ref(null);
const secondcategory = ref(null);
const thirdcategory = ref(null);
const categoryId = ref(null);
const allManufacturerList = ref([]);
const topSortBarParams = ref({});
const hotWordsId = ref('');
const paginationListRef = ref(null);
const subSiteRef = ref(null);
const searchTrackKey = ref('');
const searchResultTotal = ref(0);

const activeFilter = computed(() => !!queryParams.manufacturer);
const hasFilter = computed(() => (topSortBarParams.value && topSortBarParams.value.sortField != 'default') || queryParams.manufacturer);
const typeContainer = computed(() => (footerType.value == 'main' ? 'main-site' : 'child-site'));
const mainTitle = computed(() => (hasFilter.value ? '综合排序' : '推荐排序'));
const ifMan = computed(() => !(companyId.value && String(companyId.value) !== '1')); // 是否主站
const showCategoryTitle = computed(() => !!categoryId.value);

/** 是否来自分类浏览（非搜索框搜索），与 seo-main from=category 对齐 */
const isCategoryBrowse = () => ['classify', 'category', 'childSite'].includes(pageFrom.value);

/** 搜索模块 search_query 埋点（attrCode/attrName 与移动端其他埋点一致） */
const reportSearchQuery = async total => {
	const keyword = titles.value?.trim();
	if (!keyword || isCategoryBrowse()) return;

	const currentTrackKey = `${keyword}|${route.query.searchType ?? ''}|${hotWordsId.value ?? ''}`;
	if (searchTrackKey.value === currentTrackKey) return;

	const searchType = Number(route.query.searchType);
	const isSuggestion = searchType === 2 || !!hotWordsId.value;
	const isSubsite = companyId.value && String(companyId.value) !== '1';
	const channelType = isSubsite ? '子站搜索框' : '总站搜索框';

	const eventData = {
		module: '搜索模块',
		eventCode: 'search_query',
		eventName: '搜索',
		attrName: 'channel_type',
		attrCode: channelType,
		ext3: total ?? 0,
		ext4: keyword,
		...(isSuggestion ? { ext2: keyword } : { ext1: keyword }),
	};

	if (isSubsite) {
		const companyInfo = await getCompanyInfo(null, null, companyId.value);
		eventData.companyCode = companyInfo?.companyCode || companyCode.value || '';
		eventData.companyName = companyInfo?.websiteName || companyInfo?.companyName || '';
	}

	trackEvent(eventData);
	if (import.meta.client) {
		searchTrackKey.value = currentTrackKey;
	}
};

const handleSortClick = async data => {
	try {
		const obj = {};
		if (data.sortField == 'site') {
			const selectCompany = await subSiteRef.value?.open();
			if (selectCompany) {
				obj.companyId = selectCompany.companyId;
			}
		} else {
			obj.sortField = data.sortField;
			if (data.sortField === 'price' || data.sortField === 'onSaleTime') {
				obj.sort = data.sort;
			}
		}
		topSortBarParams.value = obj;
		reloadList();
	} catch (error) {}
};

const toggleTo = type => {
	viewType.value = type;
};

const updateDataList = searchVal => {
	showPopup.value = false;
	queryParams.manufacturer = searchVal;
	reloadList();
};

const showDrawer = () => {
	showPopup.value = true;
};

const getAllGoodsPrice = async templateGoods => {
	const arr = templateGoods;
	const arrayPrice = [];
	arr.forEach(item => arrayPrice.push(item.productid));
	if (!arrayPrice.length) return arr;
	if (!canFetchGoodsPricePre()) return arr;
	const res = await getGoodsPricePre(arrayPrice);
	if (Number(res?.code) !== 200 || !Array.isArray(res?.data)) return arr;
	res.data.forEach(item1 => {
		arr.forEach(item2 => {
			if (item2.productid === item1.productid) {
				// 统一回填大小写价格字段，后续合作价与普通价都从商品对象自身读取。
				mergeProductPriceFields(item2, item1);
				item2.collectionId = item1.collectionId;
				item2.agreementPriceId = item1.agreementPriceId;
				item2.deliveryDetail = item1.deliveryDetail;
			}
		});
	});
	return arr;
};

const goBack = () => {
	navigateBack();
};

const reloadList = async () => {
	await nextTick();
	paginationListRef.value?.clearList?.();
	paginationListRef.value?.onRefresh?.();
};

// 参考 app/pages/gyInfo/list.vue：先初始化首屏参数，再 useAsyncData 预取首屏列表
const initFromRoute = () => {
	const params = route.query;

	// 登录态：SSR 读 cookie；CSR 首屏不读 localStorage（避免水合结构变化），mounted 后再补齐
	if (import.meta.server) {
		const cmdc_access_token_mobile = useCookie('cmdc_access_token_mobile');
		isLogin.value = !!cmdc_access_token_mobile.value;
	}

	companyId.value = props.initialCompanyId !== '' && props.initialCompanyId != null ? String(props.initialCompanyId) : params.companyId || 1;
	footerType.value = companyId.value && String(companyId.value) !== '1' ? 'child' : 'main';

	if (params.key && !params.keyword) titles.value = params.key;
	if (params.keyword) titles.value = params.keyword;
	placeholders.value = params.placeholders ? params.placeholders : '产品名称/型号规格/厂家信息/商品描述';
	if (params.productnature) productnature.value = params.productnature;
	if (params.firstcategory) firstcategory.value = params.firstcategory;
	if (params.secondcategory) secondcategory.value = params.secondcategory;
	if (params.thirdcategory) thirdcategory.value = params.thirdcategory;
	if (route.params.categoryId) {
		categoryId.value = String(route.params.categoryId);
		titles.value = props.initialCategoryTitle || categoryDetailTitleState.value || '分类详情';
	}
	if (params.pageFrom) pageFrom.value = params.pageFrom;

	Object.assign(queryParams, {
		filterConditions: [],
		companyCode: companyCode.value || '',
		ranking: '',
		searchName: titles.value || '',
		pageNum: 1,
		pageSize: PAGE_SIZE,
		companyId: companyId.value || '',
		orderBy: '',
		orderByType: '',
	});
	if (categoryId.value) {
		delete queryParams.searchName;
	}
	if (params.hotWordsId) {
		hotWordsId.value = params.hotWordsId;
		queryParams.hotWordsId = params.hotWordsId;
		delete queryParams.searchName;
	}
};

/** BigImg/SmallImg 列表数据源：getMobileCategoryFromSearch（商品搜索列表接口） */
/** 实际请求逻辑（用于 CSR 分页 & SSR 首屏预取） */
const fetchProductListApi = async (pageNum, pageSize) => {
	try {
		const companyInfo = await getCompanyInfo(null, null, companyId.value);
		companyCode.value = companyInfo?.companyCode || '';
		Object.assign(queryParams, {
			filterConditions: [],
			companyCode: companyCode.value || '',
			ranking: '',
			searchName: titles.value || '',
			pageNum: pageNum,
			pageSize: pageSize,
			companyId: companyId.value || '',
			orderBy: '',
			orderByType: '',
			...(topSortBarParams.value || {}),
		});
		if (categoryId.value) {
			delete queryParams.searchName;
		}

		// 主站 companyId=1 时不传 companyId

		if (String(queryParams.companyId) === '1') {
			delete queryParams.companyId;
		}
		if (hotWordsId.value) {
			queryParams.hotWordsId = hotWordsId.value;
			delete queryParams.searchName;
		}

		if (secondcategory.value != 'null') {
			queryParams.filterConditions.push({ key: 'secondcategory', value: secondcategory.value });
		}
		if (thirdcategory.value != 'null') {
			queryParams.filterConditions.push({ key: 'thirdcategory', value: thirdcategory.value });
		}
		if (productnature.value != 'null') {
			queryParams.productnature = productnature.value;
		}

		const reqParams = { ...queryParams };
		if (categoryId.value) {
			const categoryIdKey = 'subCategoryId';
			reqParams[categoryIdKey] = categoryId.value;
			// 分类详情与关键词搜索互斥，分类请求只能使用 subCategoryId。
			delete reqParams.searchName;
		}
		if (reqParams.sortField == 'sales') {
			reqParams.salesVolumeOrder = 0;
		} else if (reqParams.sortField == 'onSaleTime') {
			reqParams.orderBy = 'uploadtime';
			reqParams.orderByType = reqParams.sort == 'DESC' ? '0' : reqParams.sort == 'ASC' ? '1' : null;
		} else {
			reqParams.salesVolumeOrder = null;
		}
		reqParams.ranking = reqParams.sortField == 'price' ? (reqParams.sort == 'DESC' ? '0' : '1') : null;

		let res = await getMobileCategoryFromSearch(reqParams);
		if (res?.code != '200') throw new Error(res?.message || '获取列表失败');

		if (pageNum === 1) {
			searchResultTotal.value = res.data?.total ?? 0;
			if (import.meta.client) {
				reportSearchQuery(searchResultTotal.value);
			}
		}
		if (categoryId.value) {
			const detailTitle = getDetailTitleFromResponse(res?.data);
			titles.value = detailTitle;
			categoryDetailTitleState.value = detailTitle;
		}

		if (!queryParams.manufacturer) {
			allManufacturerList.value = cloneDeep(res.data.manufacturer || []);
		}

		const mutilList = await getMutil({ f: true });
		if (import.meta.client) {
			setStorageSync('getMutilAll', mutilList.data);
		}

		const listProductIds = [];
		res.data.list.forEach(item1 => {
			listProductIds.push({
				productCode: item1.productcode ? item1.productcode : item1.productCode,
				productId: item1.productid,
				companyCode: item1.filialecode,
			});
			const items = mutilList.data.find(x => x.companyCode == item1.filialecode);
			if (items) {
				item1.companyId = items.companyId;
				item1.websiteName = items.websiteName;
				item1.companyName = items.companyName;
				item1.defaultImageUrl = item1.defaultimageurl || item1.defaultImageUrl;
			}
		});

		const resCoupons = await getDiscountList(listProductIds);
		if (resCoupons.code === '200') {
			res.data.list.forEach(item1 => {
				resCoupons.data.forEach(item2 => {
					if (item1.productid === item2.productId) {
						item1.zero = item2.zero;
						item1.coupon = item2.coupon;
						if (item2.activity) item1['activityType'] = item2.activity.activityType;
					}
				});
			});
		}

		let arr = res.data.list;
		if (canFetchGoodsPricePre()) {
			arr = await getAllGoodsPrice(res.data.list);
		}
		arr.forEach(x => {
			x.productId = x.productId || x.productid;
		});
		return arr;
	} catch (e) {
		throw e;
	}
};

// SSR 首屏预取（参考 gyInfo/list.vue 的 initial-list 用法）
initFromRoute();
const { data: ssrListRes } = await useAsyncData(`productList-firstpage-${route.fullPath}`, async () => {
	try {
		const list = await fetchProductListApi(1, PAGE_SIZE);
		return { code: 200, data: { list, total: searchResultTotal.value } };
	} catch (err) {
		return { code: 500, message: err?.message ?? '请求失败', data: { list: [], total: 0 } };
	}
});

console.log('ssrListRes', ssrListRes.value);
if (import.meta.client && Number(ssrListRes.value?.code) !== 200) {
	showToast({ message: ssrListRes.value?.message ?? '请求失败', duration: 3000 });
}

// PaginationList 的 fetch-data：客户端请求分页；服务端直接复用 SSR 首屏列表
const fetchProductList = async (pageNum, pageSize) => {
	if (import.meta.client) {
		return await fetchProductListApi(pageNum, pageSize);
	}
	return ssrListRes.value?.data?.list ?? [];
};

onMounted(async () => {
	// SSR 首屏已在服务端拉取列表，客户端补报搜索埋点
	if (titles.value?.trim() && !isCategoryBrowse()) {
		await reportSearchQuery(ssrListRes.value?.data?.total ?? searchResultTotal.value);
	}

	// CSR：补齐登录态（localStorage）
	isLogin.value = !!getStorageSync('cmdc_access_token_mobile');

	// 已登录时刷新首屏列表，补拉价格（SSR 不调用 getGoodsPricePre）
	if (canFetchGoodsPricePre()) {
		await reloadList();
	}

	// CSR：补齐 companyCode（依赖 localStorage 的 getCompanyInfo）
	const companyInfo = await getCompanyInfo(null, null, companyId.value);
	if (import.meta.client) {
		setStorageSync('searchCompanyCode', companyInfo?.companyCode || '');
	}
	companyCode.value = companyInfo?.companyCode || '';

	if (isLogin.value && companyId.value != '1') {
		await getUserSite({
			companyId: companyId.value,
			companyCode: companyCode.value,
		});
	}

	// CSR：将 companyCode 同步到 queryParams（后续分页请求会用到）
	queryParams.companyCode = companyCode.value || '';

	useWeixin().setShareInfo({
		title: '国械在线-商品搜索列表',
		desc: '我正在看【国械在线-商品搜索列表】，分享给你啦。',
		img: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
	});
});
</script>

<style lang="scss" scoped>
.paging {
	height: calc(100vh - 7rem);
	overflow: auto;
	padding-bottom: 0.5rem;
}
.subWrap {
	position: relative;
	width: 100%;
	height: 100%;
}
.top-head {
	display: flex;
	align-items: center;
	background-color: #fff;
	height: 3.67rem;
	padding: 0 12px 0 12px;
	padding-top: 0.5rem;
}

.icon-gongneng_shaixuan {
	font-size: 17px !important;
	margin-left: 0.5rem;
	padding-right: 12px;
	&.active {
		color: #566adc;
	}
}

.title-row {
	display: flex;
	flex: 1;
	justify-content: center;
	font-size: 18px;
	font-weight: 500;
	align-items: center;
	color: #333333;
}
.child-site :deep(.sort-key.site_field) {
	display: none !important;
}
.main-site :deep(.sort-key.onSaleTime_field) {
	display: none !important;
}
</style>
